<script lang="js" setup>
import { useFutureStore } from '~/store/future.js'
import { usePanelStore } from '~/store/panel'
import { onlyAllowNumber } from '~/utils/tools'
import { useTpsl } from '~/hooks/tpsl'
import { sub } from '~/utils/math'

const props = defineProps({
  // 点击高级
  onAdvance: {
    type: Function,
    required: true,
  },
})

const initTpsl = {
  triggerValueType: 'ror',
  priceType: 'MARKET',
  sliderValue: 0,
  triggerPrice: '',
  triggerValue: '',
  price: '',
  orderSubType: 2,
  error: '',
}

const TpSlModal = defineAsyncComponent(() => import('../modal/tpsl/index.vue'))

const { t } = useI18n()
const panel = usePanelStore()
const future = useFutureStore()
const { inferPriceByValue } = useTpsl()

const tpslModalOpen = ref(false)
const entrustInfo = ref(null)
const tp = ref({ ...initTpsl })
const sl = ref({ ...initTpsl })
const check = ref(false)
const side = ref(1)

const triggerValuOpts = computed(() => [
  {
    label: `${t('价格')}(USDT)`,
    unitName: future.baseM,
    key: 'price',
  },
  {
    label: `${t('收益率')}(%)`,
    unitName: '%',
    key: 'ror',
  },
  {
    label: `${t('涨跌幅')}(%)`,
    unitName: '%',
    key: 'pcp',
  },
  {
    label: `${t('盈亏')}(USDT)`,
    unitName: 'USDT',
    key: 'pnL',
  },
])

// 委托价格
const op = computed(() => panel.isMarket ? '' : panel.price)

const isAdvance = computed(() => !!entrustInfo.value)

watch(() => tp.value.triggerValueType, () => {
  tp.value.triggerValue = ''
})
watch(() => sl.value.triggerValueType, () => {
  sl.value.triggerValue = ''
})
watch(check, (nv) => {
  if (!nv) {
    reset()
  }
})

watch(() => future.accountType, (nv) => {
  if (nv === 30) {
    check.value = false
  }
})

function updateSlTriggerValue(value) {
  if (sl.value.triggerValueType !== 'price') {
    if (value === '-') {
      sl.value.triggerValue = ''
    }
    else if (!value?.includes('-')) {
      sl.value.triggerValue = `-${value}`
    }
    else {
      sl.value.triggerValue = value
    }
  }
  else {
    sl.value.triggerValue = value
  }
}

function formatPrice(value) {
  return +value <= 0 ? '--' : fixRaw(value, future.curInfo.priceScale)
}

function confirm(data) {
  entrustInfo.value = data
  tpslModalOpen.value = false
}

async function openTpslModal() {
  if (props?.onAdvance()) {
    tpslModalOpen.value = true
  }
}

function reset() {
  entrustInfo.value = null
  tp.value = { ...initTpsl }
  sl.value = { ...initTpsl }
}

function getOrderConditions(side) {
  const data = []
  if (check.value) {
    const tpData = entrustInfo.value?.tp || { ...tp.value, triggerPrice: inferPriceByValue({ ...tp.value, side, lots: panel.getPanelLots(1) }) }
    const slData = entrustInfo.value?.sl || { ...sl.value, triggerPrice: inferPriceByValue({ ...sl.value, side, lots: panel.getPanelLots(-1) }) }
    tpData.triggerPrice && data.push({
      orderType: tpData.priceType === 'MARKET' ? 3 : 1,
      stopCondition: 1,
      price: tpData.priceType === 'MARKET' ? '' : fixRaw(tpData.price, future.ps),
      stopPrice: fixRaw(tpData.triggerPrice, future.ps),
    })
    slData.triggerPrice && data.push({
      orderType: slData.priceType === 'MARKET' ? 3 : 1,
      stopCondition: 2,
      price: slData.priceType === 'MARKET' ? '' : fixRaw(slData.price, future.ps),
      stopPrice: fixRaw(slData.triggerPrice, future.ps),
    })
  }
  return data
}

function allowInput(value, type) {
  let dp = 2
  if (type === 1) {
    // 止盈
    dp = tp.value.triggerValueType === 'price' ? future.ps : 2
    return onlyAllowNumber(value, dp)
  }
  else {
    dp = sl.value.triggerValueType === 'price' ? future.ps : 2
    return onlyAllowNumber(value, dp, true)
  }
}

function validate(side) {
  const t1 = side === 1 ? t('多头') : t('空头')
  const op = panel.getPriceBySide({ side }) // 开仓价格

  const fn = (tpslSide) => {
    const model = tpslSide === 1 ? tp : sl
    const t2 = tpslSide === 1 ? t('止盈') : t('止损')

    if (model.value.triggerValue) {
      const p = inferPriceByValue({ ...model.value, side, lots: panel.getPanelLots(side) })
      if (model.value.triggerValueType === 'price') {
        // console.log('@@@', +p, +op, tpslSide, side)
        // 多头仓位的止盈价格请大于开仓均价
        if (sub(p, op).mul(side).mul(tpslSide).lt(0)) {
          const t3 = side * tpslSide === 1 ? t('大于') : t('小于')
          model.value.error = t('{0}仓位的{1}价格请{2}开仓均价', [t1, t2, t3])
          return false
        }
      }
      else {
        if (+p <= 0) {
          const t4 = side === 1 ? t('做多') : t('做空')
          // 当前做多，无法得到正确的止损触发价价格需大于0
          model.value.error = t('目前{0}，無法得到正確的{1}觸發價價格必需大於 0', [t4, t2])
          return false
        }
      }
    }
    return true
  }

  return fn(1) && fn(-1)
}

defineExpose({
  getOrderConditions,
  reset,
  validate,
  check,
  side,
  isAdvance,
})
</script>

<template>
  <div v-if="panel.pe === 1">
    <div class="flex items-center justify-between h-5">
      <common-x-checkbox v-model="check" size="small" :disabled="[30].includes(future.accountType)" :class="{ '!cursor-not-allowed': [30].includes(future.accountType) }">
        <span class="text-[10px] font-medium">{{ $t('止盈/止损') }}</span>
        <span v-if="entrustInfo">({{ side === 1 ? $t('多') : $t('空') }})</span>
        <CommonXTipsSimple :text="$t('在开仓前设置止盈止损委托，您可以选择使用最新价格或标记价格作为触发价')">
          <svgo-common-icon-tip class="w-3 h-3 ms-1 outline-none" />
        </CommonXTipsSimple>
      </common-x-checkbox>
      <template v-if="check">
        <svgo-futures-icon-tpsl-edit v-if="entrustInfo" class="w-3 h-3 cursor-pointer" @click="() => tpslModalOpen = true" />
        <button v-else class="text-primary text-[10px]" @click="openTpslModal">
          {{ $t('高级') }}
        </button>
      </template>
    </div>
    <div v-if="check" class="mt-3">
      <div v-if="entrustInfo" class="p-3 space-y-1 text-xs rounded-md bg-pageTertiary">
        <template v-if="entrustInfo?.tp.triggerPrice">
          <div class="flex items-center justify-between">
            <div class="text-textSecondary">
              {{ $t('触发止盈') }}
            </div>
            <div class="font-medium">
              {{ entrustInfo.tp.triggerPrice }}USDT
            </div>
          </div>
          <div class="flex items-center justify-between">
            <div class="text-textSecondary">
              {{ $t('止盈委托') }}
            </div>
            <div class="font-medium">
              {{ entrustInfo.tp.priceType === 'MARKET' ? `${$t('市价')}` : `${$t('限价')} ${entrustInfo.tp.price} USDT` }}
            </div>
          </div>
        </template>
        <template v-if="entrustInfo?.sl.triggerPrice">
          <div class="flex items-center justify-between">
            <div class="text-textSecondary">
              {{ $t('触发止损') }}
            </div>
            <div class="font-medium">
              {{ entrustInfo.sl.triggerPrice }}USDT
            </div>
          </div>
          <div class="flex items-center justify-between">
            <div class="text-textSecondary">
              {{ $t('止损委托') }}
            </div>
            <div class="font-medium">
              {{ entrustInfo.sl.priceType === 'MARKET' ? `${$t('市价')}` : `${$t('限价')} ${entrustInfo.sl.price} USDT` }}
            </div>
          </div>
        </template>
      </div>
      <div v-else>
        <n-tooltip placement="top-end" trigger="focus" :class="{ hidden: tp.triggerValueType === 'price' }">
          <template #default>
            <div class="text-xs">
              <p>{{ $t('买入时止盈价') }} <span class="text-buy">{{ formatPrice(inferPriceByValue({ ...tp, side: 1, lots: panel.getPanelLots(1) })) }}</span></p>
              <p class="mt-1">
                {{ $t('卖出时止盈价') }} <span class="text-sell">{{ formatPrice(inferPriceByValue({ ...tp, side: -1, lots: panel.getPanelLots(-1) })) }}</span>
              </p>
            </div>
          </template>
          <template #trigger>
            <common-x-opt-input
              v-model:value="tp.triggerValue" :error="tp.error" placeholder="" :is-button="false" :allow-input="value => allowInput(value, 1)" style="--n-height:40px;"
              class="[&_.n-input-wrapper]:!overflow-visible"
              @focus="tp.error = ''"
            >
              <template #prefix>
                <span class="font-medium text-textTertiary">{{ $t('止盈') }}</span>
              </template>
              <template #suffix>
                <common-x-select v-model="tp.triggerValueType" :list="triggerValuOpts" current-class="text-textPrimary" />
              </template>
            </common-x-opt-input>
          </template>
        </n-tooltip>
        <n-tooltip placement="top-end" trigger="focus" :class="{ hidden: sl.triggerValueType === 'price' }">
          <template #default>
            <div class="text-xs">
              <p>{{ $t('买入时止损价') }} <span class="text-buy">{{ formatPrice(inferPriceByValue({ ...sl, side: 1, lots: panel.getPanelLots(1) })) }}</span></p>
              <p class="mt-1">
                {{ $t('卖出时止损价') }} <span class="text-sell">{{ formatPrice(inferPriceByValue({ ...sl, side: -1, lots: panel.getPanelLots(-1) })) }}</span>
              </p>
            </div>
          </template>
          <template #trigger>
            <common-x-opt-input
              :is-button="false" style="--n-height:40px;"
              :value="sl.triggerValue"
              :on-update:value="updateSlTriggerValue"
              :allow-input="value => allowInput(value, -1)"
              placeholder=""
              :error="sl.error"
              class="[&_.n-input-wrapper]:!overflow-visible mt-3"
              @focus="sl.error = ''"
            >
              <template #prefix>
                <span class="font-medium text-textTertiary">{{ $t('止损') }}</span>
              </template>
              <template #suffix>
                <common-x-select v-model="sl.triggerValueType" :list="triggerValuOpts" current-class="text-textPrimary" />
              </template>
            </common-x-opt-input>
          </template>
        </n-tooltip>
      </div>
    </div>
    <TpSlModal v-model:show="tpslModalOpen" v-model:side="side" :default-value="entrustInfo ? entrustInfo : { tp, sl }" :price="op" :lots="panel.getPanelLots(side)" @confirm="confirm" />
  </div>
</template>
